import React, { useEffect, useMemo } from 'react';
import { Col, Row } from 'react-bootstrap';
import { ApiScenarioEntityType } from '../api';
import { Dropdown } from '@salvapp/ui-components';
import { DropdownOption } from '@salvapp/ui-components/build/dropdown/dropdown.types';
import { connect, ResolveThunks } from 'react-redux';
import { AppState } from '../store/store';
import {
  fetchReasons as fetchReasonsAction,
  fetchReasonsV2 as fetchReasonsV2Action,
} from '../scenario/scenariosActions';
import { setPage } from './alertReducer';
import { SelectOption } from './filters/filtersReducer';
import {
  updateSelectedAssigneeAction,
  updateSelectedMultiReasonAction,
  updateSelectedScenarioTypeAction,
  updateSelectedStatusAction,
} from './filters/filtersActions';
import { ALL_VALUE } from './helper';
import { useNavigate } from 'react-router';
import { getUserId, getUserName } from '../auth/authReducer';
import { ALERT_STATUS_PARAM, getFilterAlertStatuses, statusSelectedOrURLOrDefault } from './alertStatusHelper';
import { getFilterScenarioTypes, SCENARIO_TYPE_PARAM, scenarioTypeSelectedOrURLOrDefault } from './scenarioTypeHelper';
import { joinScenarioIds, multiReasonsSelectedOrURLOrDefault, SCENARIO_IDS_PARAM } from './reasonsHelper';
import { ASSIGNEE_ID_PARAM, assigneeIdSelectedOrURLOrDefault } from './assigneeHelper';
import { useQueryUsersBasic } from '../users/userActions';
import { InternalApiUserBasicDetails } from '../api-internal';

export const TransactionAlertsFilter = ({
  statusOptions,
  allStatuses,
  setPage,
  fetchReasonsV2,
  reasons,
  scenarioTypes,
  currentUser,
  filters,
  updateSelectedStatus,
  updateSelectedAssignee,
  updateSelectedMultiReason,
  updateSelectedScenarioType,
}: ComponentProps) => {
  const actions = {
    STATUS_CHANGE: 'STATUS_CHANGE',
    SCENARIO_TYPE_CHANGE: 'SCENARIO_TYPE_CHANGE',
    MULTI_REASON_CHANGE: 'MULTI_REASON_CHANGE',
    ASSIGNEE_CHANGE: 'ASSIGNEE_CHANGE',
  } as const;

  const { data: users } = useQueryUsersBasic();

  useEffect(() => {
    fetchReasonsV2(ApiScenarioEntityType.TRANSACTION);
  }, [fetchReasonsV2]);

  const navigate = useNavigate();
  const updateHistory = (updateFunc: (params: URLSearchParams) => void) => {
    const params = new URLSearchParams(location.search);
    updateFunc(params);
    navigate({
      search: `?${params.toString()}`,
    });
  };

  const filterChange = (actionType: string, value: DropdownOption[]) => {
    setPage(0);

    switch (actionType) {
      case actions.STATUS_CHANGE:
        updateSelectedStatus(value[0]);
        updateHistory((params) => {
          params.set(ALERT_STATUS_PARAM, value[0].value);
        });
        break;
      case actions.SCENARIO_TYPE_CHANGE:
        updateSelectedScenarioType(value[0]);
        updateHistory((params) => {
          params.set(SCENARIO_TYPE_PARAM, value[0].value);
        });
        break;
      case actions.MULTI_REASON_CHANGE:
        updateSelectedMultiReason(value);
        updateHistory((params) => {
          const elementsAdded = value as DropdownOption[];
          const elementAdded = elementsAdded[elementsAdded.length - 1];
          if (elementAdded === undefined || elementAdded.key === ALL_VALUE) {
            params.delete(SCENARIO_IDS_PARAM);
            return;
          }

          const ids = joinScenarioIds(value);
          if (ids !== undefined) {
            params.set(SCENARIO_IDS_PARAM, ids);
          } else {
            params.delete(SCENARIO_IDS_PARAM);
          }
        });
        break;
      case actions.ASSIGNEE_CHANGE:
        updateSelectedAssignee(value[0]);
        updateHistory((params) => {
          params.set(ASSIGNEE_ID_PARAM, value[0].value);
        });
        break;
    }
  };

  const userValues: DropdownOption[] = [
    {
      key: ALL_VALUE,
      value: ALL_VALUE,
      label: 'All users',
    },
    {
      key: currentUser.key,
      value: currentUser.value,
      label: 'Assigned to me',
    },
  ].concat(
    users?.map((u: InternalApiUserBasicDetails) => ({
      key: String(u.id),
      value: String(u.id),
      label: u.email || '',
    })) || []
  );

  const renderReasonSelector = useMemo(() => {
    return (
      <Col md={12} className='multi-reason-col'>
        <Dropdown
          name='reason'
          label='Reason'
          placeholder='All reasons placeholder'
          options={reasons}
          value={multiReasonsSelectedOrURLOrDefault(reasons, filters.selectedMultiReason)}
          isMulti={true}
          onChange={(options: DropdownOption[]) => {
            filterChange(actions.MULTI_REASON_CHANGE, options);
          }}
        />
      </Col>
    );
  }, [JSON.stringify(reasons), filters.selectedMultiReason]);

  const renderAssignedToFilter = useMemo(() => {
    return (
      <Col md={4}>
        <Dropdown
          name='user'
          label='Assigned to'
          options={userValues}
          value={assigneeIdSelectedOrURLOrDefault(userValues, filters.selectedAssignee)}
          onChange={(value: DropdownOption[]) => {
            filterChange(actions.ASSIGNEE_CHANGE, value);
          }}
        />
      </Col>
    );
  }, [userValues, filters.selectedAssignee]);

  const renderAlertStatusFilter = useMemo(() => {
    return (
      <Col md={4}>
        <Dropdown
          name='status'
          label='Alert status'
          options={statusOptions}
          value={statusSelectedOrURLOrDefault(filters.selectedStatus, allStatuses)}
          onChange={(value: DropdownOption[]) => {
            filterChange(actions.STATUS_CHANGE, value);
          }}
        />
      </Col>
    );
  }, [statusOptions, filters.selectedStatus]);

  const renderScenarioTypeFilter = useMemo(() => {
    return (
      <Col md={4}>
        <Dropdown
          name='scenarioType'
          label='Scenario type'
          options={scenarioTypes}
          value={scenarioTypeSelectedOrURLOrDefault(filters.selectedScenarioType)}
          onChange={(value: DropdownOption[]) => {
            filterChange(actions.SCENARIO_TYPE_CHANGE, value);
          }}
        />
      </Col>
    );
  }, [scenarioTypes, filters.selectedScenarioType]);

  return (
    <div className='header'>
      <Row>
        {renderAssignedToFilter}
        {renderAlertStatusFilter}
        {renderScenarioTypeFilter}
      </Row>
      <Row className='mt-3'>{renderReasonSelector}</Row>
    </div>
  );
};

const mapStateToProps = (state: AppState) => {
  const { scenarios, filters, screeningAlertStatuses } = state;

  return {
    currentUser: {
      key: getUserName(state) || ALL_VALUE,
      value: String(getUserId(state) || ALL_VALUE),
      label: getUserName(state) || 'All',
    },
    statusOptions: getFilterAlertStatuses(screeningAlertStatuses.statuses),
    allStatuses: screeningAlertStatuses.statuses,
    reasons: [
      {
        key: ALL_VALUE,
        value: ALL_VALUE,
        label: 'All reasons',
      },
    ].concat(
      scenarios.reasonsWithScenarioIDs.map((reason) => ({
        key: reason.name,
        value: reason.scenarioIds.join(','),
        label: reason.name,
      }))
    ),
    scenarioTypes: getFilterScenarioTypes(),
    filters: filters,
  };
};

const mapDispatchToProps = {
  fetchReasons: fetchReasonsAction,
  fetchReasonsV2: fetchReasonsV2Action,
  setPage,
  updateSelectedMultiReason: (selected: SelectOption[]) => updateSelectedMultiReasonAction(selected),
  updateSelectedStatus: (selected: SelectOption) => updateSelectedStatusAction(selected),
  updateSelectedAssignee: (selected: SelectOption) => updateSelectedAssigneeAction(selected),
  updateSelectedScenarioType: (selected: SelectOption) => updateSelectedScenarioTypeAction(selected),
};

type ComponentProps = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export default connect(mapStateToProps, mapDispatchToProps)(TransactionAlertsFilter);
